"use client";

import Link from "next/link";
import { useRef, useState } from "react";
import {
  recordRemedyPractice,
  type AccountRemedyPlan,
  type RemedyPracticeResponse,
} from "./account-api";
import { accountLocalCivilDate } from "./account-format";

interface PendingPractice {
  signature: string;
  idempotencyKey: string;
}

export function AccountRemedyPlans({
  onRefresh,
  plans,
  token,
  verified,
}: {
  onRefresh: () => Promise<void>;
  plans: AccountRemedyPlan[];
  token: string;
  verified: boolean;
}) {
  if (!verified || !plans.length) return null;
  return (
    <section
      aria-labelledby="remedy-plans-heading"
      className="mb-6 rounded-2xl border border-[var(--color-accent-border)] bg-[var(--color-surface-warm)] p-5 shadow-[var(--shadow-elevation-low)] sm:p-6"
    >
      <p className="text-eyebrow font-semibold uppercase tracking-eyebrow text-[var(--color-accent)]">
        Practice, then observe
      </p>
      <h2 id="remedy-plans-heading" className="mt-2 font-serif text-2xl">
        Your upay plans
      </h2>
      <p className="mt-2 text-sm leading-6 text-[var(--color-text-sub)]">
        These trackers organize the exact upays already saved in your readings. They do not add a
        new mantra, count, gemstone, date or promised result.
      </p>
      <div className="mt-5 grid gap-4">
        {plans.slice(0, 3).map((plan) => (
          <RemedyPlanCard
            key={plan.prescription_id}
            plan={plan}
            token={token}
            onRefresh={onRefresh}
          />
        ))}
      </div>
    </section>
  );
}

function RemedyPlanCard({
  onRefresh,
  plan,
  token,
}: {
  onRefresh: () => Promise<void>;
  plan: AccountRemedyPlan;
  token: string;
}) {
  const progress = Math.min(100, Math.round((plan.completed_count / plan.completion_target) * 100));
  return (
    <article className="rounded-xl border border-[var(--color-border)] bg-[var(--color-surface-raised)] p-4">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <p className="text-xs font-semibold uppercase tracking-wide text-[var(--color-accent)]">
            {plan.cadence_label}
          </p>
          <h3 className="mt-1 font-serif text-xl">{plan.title}</h3>
        </div>
        <span className="rounded-full bg-[var(--color-accent-light)] px-3 py-1 text-xs font-semibold text-[var(--color-accent)]">
          {plan.completed_count}/{plan.completion_target} complete
        </span>
      </div>
      <blockquote className="mt-4 border-l-2 border-[var(--color-accent)] pl-3 text-sm leading-6 text-[var(--color-text-main)]">
        {plan.remedy_text}
      </blockquote>
      <progress
        aria-label={`${plan.title} progress`}
        className="mt-4 block h-2 w-full appearance-none overflow-hidden rounded-full bg-[var(--color-border)] [&::-moz-progress-bar]:bg-[var(--color-accent)] [&::-webkit-progress-bar]:bg-[var(--color-border)] [&::-webkit-progress-value]:bg-[var(--color-accent)]"
        value={progress}
        max={100}
      >
        {progress}%
      </progress>
      <details className="mt-4">
        <summary className="cursor-pointer text-sm font-semibold text-[var(--color-text-main)]">
          See the safe practice steps
        </summary>
        <ol className="mt-3 grid list-decimal gap-2 pl-5 text-sm leading-6 text-[var(--color-text-sub)]">
          {plan.steps.map((step) => (
            <li key={step}>{step}</li>
          ))}
        </ol>
        <p className="mt-3 text-xs leading-5 text-[var(--color-text-sub)]">
          <span className="font-semibold">{plan.source_label}.</span> {plan.source_note}
        </p>
        <p className="mt-2 rounded-lg bg-[var(--color-warning-bg)] p-3 text-xs leading-5 text-[var(--color-warning-text)]">
          {plan.safety_note}
        </p>
      </details>
      <RemedyPlanAction plan={plan} token={token} onRefresh={onRefresh} />
    </article>
  );
}

function RemedyPlanAction({
  onRefresh,
  plan,
  token,
}: {
  onRefresh: () => Promise<void>;
  plan: AccountRemedyPlan;
  token: string;
}) {
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [saved, setSaved] = useState<RemedyPracticeResponse | null>(null);
  const pending = useRef<PendingPractice | null>(null);

  async function completePractice() {
    if (saving) return;
    const practiceDate = accountLocalCivilDate();
    const signature = `${plan.prescription_id}:${practiceDate}:completed`;
    if (pending.current?.signature !== signature) {
      pending.current = { signature, idempotencyKey: crypto.randomUUID() };
    }
    setSaving(true);
    setError("");
    try {
      const result = await recordRemedyPractice(
        token,
        plan.prescription_id,
        practiceDate,
        "completed",
        pending.current.idempotencyKey
      );
      setSaved(result);
      await onRefresh();
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : "Could not update this upay plan.");
    } finally {
      setSaving(false);
    }
  }

  if (!plan.practice_enabled) {
    return (
      <Link
        href={`/chat/${encodeURIComponent(plan.reading_id)}/`}
        className="mt-4 inline-flex min-h-11 items-center rounded-xl bg-[var(--color-accent)] px-4 py-2 text-sm font-semibold text-white"
      >
        Confirm with AI Jyotishi
      </Link>
    );
  }
  if (plan.status === "completed") {
    return (
      <p className="mt-4 text-sm font-semibold text-[var(--color-success-text)]">
        Starter plan completed. Use the outcome follow-up to record what changed.
      </p>
    );
  }
  return (
    <div className="mt-4">
      <button
        type="button"
        disabled={saving || saved !== null}
        onClick={() => void completePractice()}
        className="inline-flex min-h-11 items-center rounded-xl bg-[var(--color-accent)] px-4 py-2 text-sm font-semibold text-white disabled:opacity-50"
      >
        {saved ? "Practice marked" : saving ? "Saving..." : "Mark today's practice done"}
      </button>
      {error ? (
        <p role="alert" className="mt-2 text-sm text-[var(--color-error-text)]">
          {error}
        </p>
      ) : null}
    </div>
  );
}
